/**
 * How the words on a snap or a GiviSnap look: one of a few faces and one of a
 * few colours, picked while typing them.
 *
 * Kept by name rather than as a font stack or a hex code, so every check
 * along the way -- the phone, createUpload, the rules on a GiviSnap -- can
 * hold it to this list, and so a face can be swapped for a better one later
 * without rewriting anybody's snap. Carter asked for "the option to change
 * the text font and colors too".
 */

/** The faces, in the order they are offered. Classic is the app's own bold. */
export const CAPTION_FONTS = ['classic', 'serif', 'marker', 'script', 'typewriter'] as const;
export type CaptionFont = (typeof CAPTION_FONTS)[number];

/** The colours, in the order they are offered. */
export const CAPTION_COLORS = ['white', 'black', 'yellow', 'red', 'pink', 'blue', 'green', 'purple'] as const;
export type CaptionColor = (typeof CAPTION_COLORS)[number];

export interface CaptionStyle {
  font: CaptionFont;
  color: CaptionColor;
}

/** How words look until somebody picks otherwise: as they always have. */
export const CAPTION_STYLE_DEFAULT: CaptionStyle = { font: 'classic', color: 'white' };

export const isCaptionFont = (value: unknown): value is CaptionFont =>
  typeof value === 'string' && (CAPTION_FONTS as readonly string[]).includes(value);

export const isCaptionColor = (value: unknown): value is CaptionColor =>
  typeof value === 'string' && (CAPTION_COLORS as readonly string[]).includes(value);

/**
 * The style to keep, or undefined for the default -- which is never stored,
 * so a plain caption is written exactly as it was before there were styles.
 * A face or colour this list does not know falls back to the default rather
 * than costing the snap its words, as an unknown filter does.
 */
export function captionStyleOf(raw: unknown): CaptionStyle | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const { font, color } = raw as Record<string, unknown>;
  const style: CaptionStyle = {
    font: isCaptionFont(font) ? font : CAPTION_STYLE_DEFAULT.font,
    color: isCaptionColor(color) ? color : CAPTION_STYLE_DEFAULT.color,
  };
  return style.font === CAPTION_STYLE_DEFAULT.font && style.color === CAPTION_STYLE_DEFAULT.color
    ? undefined
    : style;
}
